// src/pages/Metodologia.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./Metodologia.css";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";
const LANGUAGE_CHANGE_EVENT = "zedro:languagechange";

function JourneyIcon({ type }) {
  const paths = {
    information: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5M12 8h.01" />
      </>
    ),
    route: (
      <>
        <circle cx="5" cy="18" r="2" />
        <circle cx="19" cy="6" r="2" />
        <path d="M7 18h3a3 3 0 0 0 3-3v-6a3 3 0 0 1 3-3h1" />
      </>
    ),
    action: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m8 12 2.5 2.5L16 9" />
      </>
    ),
  };

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {paths[type]}
    </svg>
  );
}

const copy = {
  es: {
    heroTitle:
      "Una metodología para convertir comprensión en decisiones, acciones y evidencia.",
    heroSubtitle:
      "Cada recorrido de ZEDRO Academy combina formación, reflexión, aplicación práctica y registro para ayudarte a comprender, ordenar, practicar y sostener lo aprendido en la vida diaria.",
    viewStore: "Explorar la Tienda",
    seeHowItWorks: "Ver cómo funciona",
    showcaseAria: "Vista previa de la metodología ZEDRO",
    altJournal: "Vista de la Bitácora ZEDRO como herramienta del ecosistema",
    altConexion: "Vista de Conexión ZEDRO como herramienta del ecosistema",

    problemLabel: "La necesidad que atendemos",
    problemTitle: "Entre comprender y avanzar suele faltar una estructura.",
    problemLead:
      "Muchas personas reúnen información, ideas e inspiración, pero necesitan un proceso que les ayude a convertirlas en decisiones, acciones y aprendizajes sostenibles.",
    problem1Title: "La información no organiza por sí sola.",
    problem1Text:
      "Puedes conocer principios valiosos y todavía necesitar claridad para relacionarlos con tus prioridades, decisiones y vida cotidiana.",
    problem2Title: "La intención necesita un proceso.",
    problem2Text:
      "Una estructura clara ayuda a pasar de querer avanzar a definir qué hacer, ponerlo en práctica y revisar lo aprendido.",
    problem3Title: "ZEDRO convierte formación en recorrido.",
    problem3Text:
      "La metodología conecta comprensión, reflexión, acción y registro dentro de una ruta progresiva y coherente.",

    methodLabel: "El método ZEDRO Academy",
    methodTitle:
      "Un ciclo claro para comprender, ordenar, practicar y sostener.",
    methodLead:
      "Cada recorrido trabaja un área concreta mediante una secuencia que une contenido, reflexión, aplicación práctica y evidencia del proceso.",
    flowAria: "Ciclo de formación ZEDRO",
    step1Title: "Comprender",
    step1Text:
      "Recibes contenido claro y preguntas que te ayudan a examinar un área concreta.",
    step2Title: "Ordenar",
    step2Text:
      "Relacionas lo aprendido con tus pensamientos, prioridades y decisiones.",
    step3Title: "Practicar",
    step3Text:
      "Conviertes lo comprendido en decisiones y acciones concretas que puedes llevar a tu realidad.",
    step4Title: "Sostener",
    step4Text:
      "Registras evidencia, revisas lo aprendido y desarrollas continuidad con responsabilidad.",
    methodStatementSmall: "No es consumo pasivo.",
    methodStatementStrong:
      "Es formación aplicada con estructura, práctica y continuidad.",

    insideLabel: "Cómo se vive la metodología",
    insideTitle:
      "Del conocimiento a una respuesta que puedes aplicar, revisar y sostener.",
    insideLead:
      "Los recorridos de ZEDRO Academy pueden adoptar formatos distintos, pero conservan una misma lógica: comprender con claridad, ordenar lo relevante, actuar con criterio y aprender de la evidencia.",
    plannedGroupLabel: "Una base común para distintos recorridos",
    plannedGroupText:
      "La metodología no depende de un producto específico. Se adapta al objetivo de cada experiencia sin perder su secuencia formativa.",
    currentToolsLabel: "Herramientas del ecosistema ZEDRO",
    currentToolsText:
      "ZEDRO Academy cuenta con herramientas que pueden apoyar distintos recorridos según el diseño y las condiciones de cada producto. Su presencia en el ecosistema no significa que todas estén incluidas en cada experiencia.",
    systemKicker: "01 · Formación guiada",
    systemTitle: "Comprender antes de actuar.",
    systemText:
      "El contenido y las preguntas de reflexión ayudan a identificar qué es relevante, comprenderlo y relacionarlo con una situación concreta.",
    dayKicker: "02 · Aplicación práctica",
    dayTitle: "Convertir comprensión en decisiones y acciones.",
    dayText:
      "Cada recorrido propone formas concretas de llevar lo aprendido a la realidad, con una participación activa y responsable de la persona.",
    missionKicker: "03 · Evidencia y aprendizaje",
    missionTitle: "Observar qué ocurrió y aprender del proceso.",
    missionText:
      "El registro permite reconocer decisiones, acciones, resultados y aprendizajes para ajustar el rumbo y sostener el avance con criterio.",
    journalKicker: "Bitácora ZEDRO",
    journalTitle: "Un espacio para conservar decisiones, acciones y aprendizajes.",
    journalText:
      "La Bitácora permite reunir registros personales y evidencia del proceso para revisarlos con perspectiva cuando una experiencia la integra.",
    connectionKicker: "Conexión ZEDRO",
    connectionTitle:
      "Un espacio guiado de reflexión y oración dentro del ecosistema.",
    connectionText:
      "Conexión ZEDRO permite explorar pasajes bíblicos relacionados con una situación, escribir una reflexión y preparar una oración personal. Su disponibilidad depende de cada producto o modalidad de acceso.",

    differenceLabel: "Qué hace diferente a ZEDRO Academy",
    differenceTitle:
      "Un proceso integrado que conecta comprensión, decisión, acción y evidencia.",
    differenceLead:
      "ZEDRO Academy organiza cada recorrido para que la formación avance hacia una respuesta concreta y deje un registro útil del proceso.",
    diff1Title: "Participación activa",
    diff1Text:
      "Cada recorrido requiere reflexión, participación y aplicación; no se limita a ver o escuchar contenido.",
    diff2Title: "Continuidad más allá de la inspiración",
    diff2Text:
      "La estructura, la repetición y la práctica ayudan a dar continuidad más allá del impulso del momento.",
    diff3Title: "Un recorrido conectado",
    diff3Text:
      "Formación, reflexión, aplicación y evidencia forman parte de una ruta intencional, aunque su formato cambie entre productos.",
    diff4Title: "Crece sobre una misma base",
    diff4Text:
      "La misma metodología permite desarrollar nuevas líneas de formación y nuevos productos sin perder coherencia institucional.",

    audienceLabel: "Para quién es",
    audienceTitle:
      "ZEDRO Academy es para quienes desean que lo que creen y aprenden se traduzca en una manera más coherente de pensar, decidir y vivir.",
    yesTitle: "Puede ser para ti si...",
    yesItems: [
      "Valoras una formación sobria y coherente, fundamentada en las Escrituras, sin simplificaciones ni fórmulas prefabricadas.",
      "Quieres desarrollar criterio y relacionar principios con decisiones y acciones concretas.",
      "Estás dispuesto a participar con responsabilidad, práctica y constancia.",
    ],
    noTitle: "Puede no ser para ti si...",
    noItems: [
      "Buscas una solución rápida o resultados garantizados.",
      "Prefieres recibir contenido sin llevarlo a la práctica.",
      "No deseas asumir una participación activa en tu proceso.",
    ],

    ctaLabel: "Da el siguiente paso",
    ctaQuote:
      "Piensa con claridad. Decide con criterio. Construye con propósito.",
    ctaText:
      "Explora los programas, herramientas y recursos de ZEDRO Academy y conoce las condiciones propias de cada experiencia.",
  },

  fr: {
    heroTitle:
      "Une méthodologie pour transformer la compréhension en décisions, en actions et en preuves concrètes.",
    heroSubtitle:
      "Chaque parcours de ZEDRO Academy combine formation, réflexion, mise en pratique et suivi afin de vous aider à comprendre, ordonner, pratiquer et soutenir ce que vous apprenez dans la vie quotidienne.",
    viewStore: "Explorer la Boutique",
    seeHowItWorks: "Voir comment cela fonctionne",
    showcaseAria: "Aperçu de la méthodologie ZEDRO",
    altJournal: "Vue de Bitácora ZEDRO comme outil de l’écosystème",
    altConexion: "Vue de Connexion ZEDRO comme outil de l’écosystème",

    problemLabel: "Le besoin auquel nous répondons",
    problemTitle:
      "Entre comprendre et avancer, il manque souvent une structure.",
    problemLead:
      "De nombreuses personnes accumulent informations, idées et inspiration, mais ont besoin d’un processus pour les transformer en décisions, en actions et en apprentissages durables.",
    problem1Title: "L’information ne s’organise pas toute seule.",
    problem1Text:
      "Vous pouvez connaître des principes précieux et avoir encore besoin de clarté pour les relier à vos priorités, à vos décisions et à votre vie quotidienne.",
    problem2Title: "L’intention a besoin d’un processus.",
    problem2Text:
      "Une structure claire aide à passer du désir d’avancer à une action définie, mise en pratique et ensuite évaluée.",
    problem3Title: "ZEDRO transforme la formation en parcours.",
    problem3Text:
      "La méthodologie relie compréhension, réflexion, action et suivi dans une progression cohérente.",

    methodLabel: "La méthode ZEDRO Academy",
    methodTitle:
      "Un cycle clair pour comprendre, ordonner, pratiquer et soutenir.",
    methodLead:
      "Chaque parcours travaille un domaine concret au moyen d’une séquence qui unit contenu, réflexion, mise en pratique et preuves du processus.",
    flowAria: "Cycle de formation ZEDRO",
    step1Title: "Comprendre",
    step1Text:
      "Vous recevez un contenu clair et des questions qui vous aident à examiner un domaine concret.",
    step2Title: "Ordonner",
    step2Text:
      "Vous reliez ce que vous apprenez à vos pensées, à vos priorités et à vos décisions.",
    step3Title: "Pratiquer",
    step3Text:
      "Vous transformez ce que vous avez compris en décisions et en actions concrètes adaptées à votre réalité.",
    step4Title: "Soutenir",
    step4Text:
      "Vous consignez des preuves, révisez ce que vous apprenez et développez une continuité responsable.",
    methodStatementSmall: "Ce n’est pas une consommation passive.",
    methodStatementStrong:
      "C’est une formation appliquée avec structure, pratique et continuité.",

    insideLabel: "Comment la méthodologie prend vie",
    insideTitle:
      "Des connaissances à une réponse que vous pouvez appliquer, évaluer et soutenir.",
    insideLead:
      "Les parcours de ZEDRO Academy peuvent prendre différentes formes, mais ils conservent une même logique : comprendre avec clarté, ordonner ce qui compte, agir avec discernement et apprendre à partir des preuves recueillies.",
    plannedGroupLabel: "Une base commune à différents parcours",
    plannedGroupText:
      "La méthodologie ne dépend pas d’un produit précis. Elle s’adapte à l’objectif de chaque expérience sans perdre sa séquence de formation.",
    currentToolsLabel: "Outils de l’écosystème ZEDRO",
    currentToolsText:
      "ZEDRO Academy dispose d’outils qui peuvent soutenir différents parcours selon la conception et les conditions de chaque produit. Leur présence dans l’écosystème ne signifie pas qu’ils sont tous inclus dans chaque expérience.",
    systemKicker: "01 · Formation guidée",
    systemTitle: "Comprendre avant d’agir.",
    systemText:
      "Le contenu et les questions de réflexion aident à reconnaître ce qui est pertinent, à le comprendre et à le relier à une situation concrète.",
    dayKicker: "02 · Mise en pratique",
    dayTitle: "Transformer la compréhension en décisions et en actions.",
    dayText:
      "Chaque parcours propose des façons concrètes de mettre les apprentissages en pratique, avec une participation active et responsable de la personne.",
    missionKicker: "03 · Preuves et apprentissage",
    missionTitle: "Observer ce qui s’est produit et apprendre du processus.",
    missionText:
      "Le suivi permet de reconnaître les décisions, les actions, les résultats et les apprentissages afin d’ajuster le parcours et de soutenir les progrès avec discernement.",
    journalKicker: "Bitácora ZEDRO",
    journalTitle:
      "Un espace pour conserver décisions, actions et apprentissages.",
    journalText:
      "Bitácora permet de réunir des notes personnelles et des preuves du processus afin de les revoir avec recul lorsqu’une expérience l’intègre.",
    connectionKicker: "Connexion ZEDRO",
    connectionTitle:
      "Un espace guidé de réflexion et de prière au sein de l’écosystème.",
    connectionText:
      "Connexion ZEDRO permet d’explorer des passages bibliques liés à une situation, de rédiger une réflexion et de préparer une prière personnelle. Sa disponibilité dépend de chaque produit ou modalité d’accès.",

    differenceLabel: "Ce qui rend ZEDRO Academy différent",
    differenceTitle:
      "Un processus intégré qui relie compréhension, décision, action et preuves concrètes.",
    differenceLead:
      "ZEDRO Academy organise chaque parcours afin que la formation mène à une réponse concrète et laisse un suivi utile du processus.",
    diff1Title: "Participation active",
    diff1Text:
      "Chaque parcours demande réflexion, participation et application; il ne se limite pas à regarder ou à écouter du contenu.",
    diff2Title: "Une continuité au-delà de l’inspiration",
    diff2Text:
      "La structure, la répétition et la pratique aident à maintenir la continuité au-delà de l’élan du moment.",
    diff3Title: "Un parcours relié",
    diff3Text:
      "Formation, réflexion, mise en pratique et preuves font partie d’un parcours intentionnel, même lorsque le format varie d’un produit à l’autre.",
    diff4Title: "Une croissance sur une même base",
    diff4Text:
      "La même méthodologie permet de développer de nouvelles lignes de formation et de nouveaux produits sans perdre la cohérence institutionnelle.",

    audienceLabel: "Pour qui",
    audienceTitle:
      "ZEDRO Academy s’adresse aux personnes qui souhaitent que leurs convictions et leurs apprentissages se traduisent par une manière plus cohérente de penser, de décider et de vivre.",
    yesTitle: "Cette démarche peut vous convenir si...",
    yesItems: [
      "Vous valorisez une formation sobre et cohérente, fondée sur les Écritures, sans simplifications ni formules toutes faites.",
      "Vous souhaitez développer votre discernement et relier des principes à des décisions et à des actions concrètes.",
      "Vous êtes prêt à participer avec responsabilité, pratique et constance.",
    ],
    noTitle: "Cette démarche peut ne pas vous convenir si...",
    noItems: [
      "Vous cherchez une solution rapide ou des résultats garantis.",
      "Vous préférez recevoir du contenu sans le mettre en pratique.",
      "Vous ne souhaitez pas participer activement à votre processus.",
    ],

    ctaLabel: "Passez à l’étape suivante",
    ctaQuote:
      "Pensez avec clarté. Décidez avec discernement. Construisez avec intention.",
    ctaText:
      "Explorez les programmes, les outils et les ressources de ZEDRO Academy et découvrez les conditions propres à chaque expérience.",
  },
};

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";
  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return savedLanguage === "fr" ? "fr" : "es";
}

export default function Metodologia() {
  const [language, setLanguage] = useState(getInitialLanguage);
  const t = copy[language] || copy.es;

  useEffect(() => {
    const syncLanguage = () => setLanguage(getInitialLanguage());

    window.addEventListener(LANGUAGE_CHANGE_EVENT, syncLanguage);
    window.addEventListener("storage", syncLanguage);

    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, syncLanguage);
      window.removeEventListener("storage", syncLanguage);
    };
  }, []);

  return (
    <main className="meth-page">
      <section className="meth-wrap" aria-labelledby="meth-title">
        <header className="meth-hero meth-hero--premium">
          <div className="meth-heroText">
            <h1 id="meth-title" className="meth-title">
              {t.heroTitle}
            </h1>

            <p className="meth-subtitle">{t.heroSubtitle}</p>

            <div className="meth-heroActions">
              <Link className="meth-btn meth-btnPrimary" to="/store">
                {t.viewStore}
              </Link>

              <a className="meth-btn meth-btnGhost" href="#como-se-vive">
                {t.seeHowItWorks}
              </a>
            </div>
          </div>

          <div className="meth-heroShowcase" aria-label={t.showcaseAria}>
            <div className="meth-heroMethod" aria-label={t.flowAria}>
              <div className="meth-heroMethod__intro">
                <span>{t.methodLabel}</span>
                <strong>{t.methodTitle}</strong>
              </div>

              <div className="meth-heroMethod__flow">
                {[t.step1Title, t.step2Title, t.step3Title, t.step4Title].map(
                  (step, index) => (
                    <div className="meth-heroMethod__step" key={step}>
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      <strong>{step}</strong>
                    </div>
                  )
                )}
              </div>

              <p>{t.methodStatementStrong}</p>
            </div>
          </div>
        </header>

        <section className="meth-card">
          <section className="meth-section meth-section--problem meth-problemBlock">
            <div className="meth-problemIntro">
              <span className="meth-sectionLabel">{t.problemLabel}</span>
              <h2 className="meth-h2">{t.problemTitle}</h2>
              <p className="meth-lead">{t.problemLead}</p>
            </div>

            <div className="meth-problemGrid">
              <div className="meth-problemCard">
                <div className="meth-problemIcon"><JourneyIcon type="information" /></div>
                <h3>{t.problem1Title}</h3>
                <p>{t.problem1Text}</p>
              </div>

              <div className="meth-problemCard">
                <div className="meth-problemIcon"><JourneyIcon type="route" /></div>
                <h3>{t.problem2Title}</h3>
                <p>{t.problem2Text}</p>
              </div>

              <div className="meth-problemCard meth-problemCard--highlight">
                <div className="meth-problemIcon"><JourneyIcon type="action" /></div>
                <h3>{t.problem3Title}</h3>
                <p>{t.problem3Text}</p>
              </div>
            </div>
          </section>

          <section className="meth-section meth-methodBlock">
            <div className="meth-methodHeader">
              <span className="meth-sectionLabel">{t.methodLabel}</span>
              <h2 className="meth-h2">{t.methodTitle}</h2>
              <p className="meth-lead">{t.methodLead}</p>
            </div>

            <div className="meth-methodFlow" aria-label={t.flowAria}>
              <article className="meth-methodStep">
                <div className="meth-methodNum">01</div><div className="meth-methodLine" />
                <h3>{t.step1Title}</h3><p>{t.step1Text}</p>
              </article>
              <article className="meth-methodStep">
                <div className="meth-methodNum">02</div><div className="meth-methodLine" />
                <h3>{t.step2Title}</h3><p>{t.step2Text}</p>
              </article>
              <article className="meth-methodStep">
                <div className="meth-methodNum">03</div><div className="meth-methodLine" />
                <h3>{t.step3Title}</h3><p>{t.step3Text}</p>
              </article>
              <article className="meth-methodStep">
                <div className="meth-methodNum">04</div>
                <h3>{t.step4Title}</h3><p>{t.step4Text}</p>
              </article>
            </div>

            <div className="meth-methodStatement">
              <span>{t.methodStatementSmall}</span>
              <strong>{t.methodStatementStrong}</strong>
            </div>
          </section>

          <section id="como-se-vive" className="meth-section meth-insideBlock">
            <div className="meth-insideHeader">
              <span className="meth-sectionLabel">{t.insideLabel}</span>
              <h2 className="meth-h2">{t.insideTitle}</h2>
              <p className="meth-lead">{t.insideLead}</p>
            </div>

            <div className="meth-experienceGroup meth-experienceGroup--planned">
              <div className="meth-experienceGroup__header">
                <span>{t.plannedGroupLabel}</span>
                <p>{t.plannedGroupText}</p>
              </div>

              <div className="meth-plannedJourney">
                <article className="meth-plannedJourney__item">
                  <span className="meth-showcaseKicker">{t.systemKicker}</span>
                  <h3>{t.systemTitle}</h3><p>{t.systemText}</p>
                </article>
                <article className="meth-plannedJourney__item">
                  <span className="meth-showcaseKicker">{t.dayKicker}</span>
                  <h3>{t.dayTitle}</h3><p>{t.dayText}</p>
                </article>
                <article className="meth-plannedJourney__item">
                  <span className="meth-showcaseKicker">{t.missionKicker}</span>
                  <h3>{t.missionTitle}</h3><p>{t.missionText}</p>
                </article>
              </div>
            </div>

            <div className="meth-experienceGroup meth-experienceGroup--current">
              <div className="meth-experienceGroup__header">
                <span>{t.currentToolsLabel}</span>
                <p>{t.currentToolsText}</p>
              </div>

              <div className="meth-showcaseList meth-showcaseList--current">
                <article className="meth-showcaseItem meth-showcaseItem--wide meth-showcaseItem--real">
                  <div className="meth-showcaseText">
                    <span className="meth-showcaseKicker">{t.journalKicker}</span>
                    <h3>{t.journalTitle}</h3><p>{t.journalText}</p>
                  </div>
                  <figure className="meth-realFigure">
                    <img src="/assets/images/metodologia/metodologia-bitacora-recorrido.png" alt={t.altJournal} />
                  </figure>
                </article>

                <article className="meth-showcaseItem meth-showcaseItem--wide meth-showcaseItem--real">
                  <div className="meth-showcaseText">
                    <span className="meth-showcaseKicker">{t.connectionKicker}</span>
                    <h3>{t.connectionTitle}</h3><p>{t.connectionText}</p>
                  </div>
                  <figure className="meth-realFigure">
                    <img src="/assets/images/metodologia/metodologia-conexion-zedro.png" alt={t.altConexion} />
                  </figure>
                </article>
              </div>
            </div>
          </section>

          <section className="meth-section meth-differenceBlock">
            <div className="meth-differenceHeader">
              <span className="meth-sectionLabel">{t.differenceLabel}</span>
              <h2 className="meth-h2">{t.differenceTitle}</h2>
              <p className="meth-lead">{t.differenceLead}</p>
            </div>

            <div className="meth-differenceGrid">
              <article className="meth-differenceCard"><span>{t.diff1Title}</span><p>{t.diff1Text}</p></article>
              <article className="meth-differenceCard"><span>{t.diff2Title}</span><p>{t.diff2Text}</p></article>
              <article className="meth-differenceCard"><span>{t.diff3Title}</span><p>{t.diff3Text}</p></article>
              <article className="meth-differenceCard meth-differenceCard--strong"><span>{t.diff4Title}</span><p>{t.diff4Text}</p></article>
            </div>
          </section>

          <section className="meth-section meth-audienceBlock">
            <div className="meth-audienceHeader">
              <span className="meth-sectionLabel">{t.audienceLabel}</span>
              <h2 className="meth-h2">{t.audienceTitle}</h2>
            </div>

            <div className="meth-cols">
              <div className="meth-col meth-col--yes">
                <h3>{t.yesTitle}</h3>
                <ul className="meth-list">{t.yesItems.map((item) => <li key={item}>{item}</li>)}</ul>
              </div>
              <div className="meth-col meth-col--no">
                <h3>{t.noTitle}</h3>
                <ul className="meth-list">{t.noItems.map((item) => <li key={item}>{item}</li>)}</ul>
              </div>
            </div>
          </section>

          <section className="meth-cta meth-ctaFinal">
            <span className="meth-sectionLabel">{t.ctaLabel}</span>
            <p className="meth-quote">{t.ctaQuote}</p>
            <p className="meth-ctaText">{t.ctaText}</p>
            <div className="meth-actions">
              <Link className="meth-btn meth-btnPrimary" to="/store">{t.viewStore}</Link>
            </div>
          </section>
        </section>
      </section>
    </main>
  );
}
